<script setup>
import { computed } from 'vue';
import { sanitize } from '@/helpers/sanitize';
import TitleHeader from '@/molecules/TitleHeader.vue';
import { IonContent, IonPage, modalController } from '@ionic/vue';

const props = defineProps({
  title: String,
  content: String,
  isHtml: {
    type: Boolean,
    default: false,
  },
});

const close = async () => {
  await modalController.dismiss(null, 'cancel');
};

function processContent(text) {
  if (!text) return '';
  const lines = text.trim().split('\n');
  let html = '';
  const listStack = [];

  const getIndentLevel = (line) => {
    const match = line.match(/^(\s*)/);
    return match ? Math.floor(match[1].length / 2) : 0;
  };
  const isListItem = (line) => /^[-*•]\s+/.test(line.trim());
  const cleanItem = (line) => line.trim().replace(/^[-*•]\s+/, '');

  lines.forEach((line) => {
    const trimmed = line.trim();
    const level = getIndentLevel(line);

    if (!trimmed) {
      while (listStack.length) {
        html += '</ul>';
        listStack.pop();
      }
      html += '<div class="my-3"></div>';
      return;
    }

    if (trimmed.startsWith('<img')) {
      while (listStack.length) {
        html += '</ul>';
        listStack.pop();
      }
      html += `<div class="my-4">${trimmed}</div>`;
      return;
    }

    if (isListItem(line)) {
      const content = cleanItem(line);
      while (listStack.length < level + 1) {
        html += '<ul class="ml-5 list-disc space-y-1">';
        listStack.push('ul');
      }
      while (listStack.length > level + 1) {
        html += '</ul>';
        listStack.pop();
      }
      html += `<li>${content}</li>`;
      return;
    }

    while (listStack.length) {
      html += '</ul>';
      listStack.pop();
    }
    html += `<div>${trimmed}</div>`;
  });

  while (listStack.length) {
    html += '</ul>';
    listStack.pop();
  }
  return html;
}

const processedContent = computed(() =>
  props.isHtml ? sanitize(props.content || '') : sanitize(processContent(props.content)),
);
</script>

<template>
  <ion-page class="page-container bg-white">
    <ion-content class="h-full">
      <div class="m-4">
        <TitleHeader :on-back="close" :tabs="[]" :title="title || '-'" class="mb-4" />
        <div
          class="modal-content text-base leading-6 text-primary font-normal"
          v-html="processedContent"
        />
      </div>
    </ion-content>
  </ion-page>
</template>

<style scoped>
/* ── Liens ── */
.modal-content :deep(a) {
  color: #2563eb;
  text-decoration: underline;
  text-underline-offset: 3px;
  word-break: break-word;
}

.modal-content :deep(a:hover) {
  color: #1d4ed8;
}

/* ── Titres ── */
.modal-content :deep(h1),
.modal-content :deep(h2),
.modal-content :deep(h3),
.modal-content :deep(h4) {
  font-weight: 600;
  line-height: 1.3;
  margin-top: 20px;
  margin-bottom: 8px;
}

.modal-content :deep(h1) {
  font-size: 1.25rem;
}
.modal-content :deep(h2) {
  font-size: 1.125rem;
}
.modal-content :deep(h3) {
  font-size: 1rem;
}

/* ── Paragraphes ── */
.modal-content :deep(p) {
  margin-bottom: 12px;
  line-height: 1.65;
}

/* ── Listes ── */
.modal-content :deep(ul),
.modal-content :deep(ol) {
  padding-left: 20px;
  margin-bottom: 12px;
}

.modal-content :deep(ul) {
  list-style-type: disc;
}

.modal-content :deep(ol) {
  list-style-type: decimal;
}

.modal-content :deep(li) {
  margin-bottom: 8px;
  line-height: 1.55;
}

.modal-content :deep(li:last-child) {
  margin-bottom: 0;
}

/* ── Texte en gras / italique ── */
.modal-content :deep(strong),
.modal-content :deep(b) {
  font-weight: 600;
}

.modal-content :deep(em),
.modal-content :deep(i) {
  font-style: italic;
}

/* ── Séparateur ── */
.modal-content :deep(hr) {
  border: none;
  border-top: 1px solid #e5e7eb;
  margin: 16px 0;
}

/* ── Images ── */
.modal-content :deep(img) {
  display: block;
  margin: 16px auto;
  max-width: 100%;
  width: 100%;
  height: auto;
  object-fit: contain;
  border-radius: 8px;
}
</style>
